import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Pause, Play } from '@phosphor-icons/react';

/**
 * Live product windows: the static windows in ./index.tsx, played as short
 * loops, the way cursor.com animates its product inside the hero painting.
 * Plain DOM and CSS keyframes (.lw-* in src/styles/statio.css), so they
 * prerender, stay crisp at any size and cost nothing to download.
 *
 * Every loop runs on one 12s clock. Elements are timed by keyframe
 * percentages (1s = 8.33%), numbers by <Ticker>, which reads the same clock,
 * so pausing the CSS also pauses the numbers. Under prefers-reduced-motion
 * nothing moves and the window shows its finished state, which is also what
 * the prerendered HTML shows. Labels match the real app (../Pugio/apps/web);
 * names and numbers are illustrative.
 */

const CYCLE = 12000;

type NavItem = 'Overview' | 'My MCPs' | 'Activity' | 'Access Control' | 'Agents';
const NAV: [string, NavItem[]][] = [
  ['MCPs', ['Overview', 'My MCPs']],
  ['Observe', ['Activity']],
  ['Organization', ['Access Control', 'Agents']],
];

/** The app shell: title bar, sidebar, page. `nav` marks the active item; `navTo` is the item the cursor clicks. */
function LiveFrame({
  label,
  url,
  urlTo,
  nav,
  navTo,
  children,
  overlay,
  className,
}: {
  label: string;
  url: string;
  urlTo?: string;
  nav: NavItem;
  navTo?: NavItem;
  children: ReactNode;
  overlay?: ReactNode;
  className: string;
}) {
  const [paused, setPaused] = useState(false);
  const offscreen = useOffscreen();
  return (
    <div ref={offscreen.ref} className={`lw ${className}`} data-paused={paused || offscreen.hidden || undefined}>
      <div className="pw lw-win" role="img" aria-label={`Animated Statio app: ${label} (illustrative data)`}>
        <div className="pw-bar" aria-hidden="true">
          <i />
          <i />
          <i />
          <span>{urlTo ? <Swap k="url" a={url} b={urlTo} /> : url}</span>
        </div>
        <div className="lw-app" aria-hidden="true">
          <nav className="lw-side">
            <b className="lw-logo">Statio</b>
            {NAV.map(([group, items]) => (
              <div key={group}>
                <small>{group}</small>
                {items.map((it) => (
                  <span key={it} className={`lw-nav${it === nav ? ' on' : ''}${it === navTo ? ' to' : ''}`}>
                    {it}
                    {it === navTo && <Cursor id="nav" />}
                  </span>
                ))}
              </div>
            ))}
          </nav>
          <div className="lw-main">{children}</div>
        </div>
      </div>
      {overlay}
      <button
        type="button"
        className="lw-toggle"
        onClick={() => setPaused((p) => !p)}
        aria-label={paused ? 'Play animation' : 'Pause animation'}
      >
        {paused ? <Play size={14} weight="fill" /> : <Pause size={14} weight="fill" />}
      </button>
    </div>
  );
}

/** True while the element is out of view or the tab is hidden, so the 12s loops stop costing CPU nobody sees. */
function useOffscreen() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    io.observe(el);
    const onVisibility = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  return { ref, hidden: !inView || !tabVisible };
}

/** Pointer that travels to its parent (position: relative) and clicks. Timing comes from the window's CSS. */
const Cursor = ({ id }: { id: string }) => (
  <span className={`lw-cur lw-cur-${id}`} aria-hidden="true">
    <span className="lw-ripple" />
    <svg width="20" height="22" viewBox="0 0 20 22">
      <path
        d="M2 1.5v16.2l4.3-4 2.9 6.6 2.8-1.2-2.8-6.5h6.1z"
        fill="var(--ink)"
        stroke="var(--surface)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const fmt = (n: number) => n.toLocaleString('en-US');
const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);

/** A number that counts from `from` to `to` between `start` and `end` seconds of the window's 12s loop. */
function Ticker({ from, to, start, end }: { from: number; to: number; start: number; end: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    const root = el?.closest('.lw');
    const clock = root?.getAnimations().find((a) => (a as CSSAnimation).animationName === 'lw-clock');
    if (!el || !clock) return; // reduced motion: keep the final value
    let raf = 0;
    let shown = '';
    const tick = () => {
      const t = (Number(clock.currentTime ?? 0) % CYCLE) / 1000;
      const p = Math.min(1, Math.max(0, (t - start) / (end - start)));
      const text = fmt(Math.round(from + (to - from) * easeOut(p)));
      if (text !== shown) el.textContent = shown = text;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, start, end]);
  return <span ref={ref}>{fmt(to)}</span>;
}

/** Two states in one cell; CSS swaps them at the window's beat. */
const Swap = ({ a, b, k }: { a: ReactNode; b: ReactNode; k: string }) => (
  <span className={`lw-swap lw-swap-${k}`}>
    <span className="lw-a">{a}</span>
    <span className="lw-b">{b}</span>
  </span>
);

/* ── 1. Access control: deny one tool, watch the call fail ───────────────── */

export function LiveAccessWindow() {
  const tools = ['db.selectRows', 'db.listTables'];
  return (
    <LiveFrame
      className="lw-access"
      label="an admin denies db.deleteRows for ci-agent, and the agent's next call to it fails with a 403 in Activity"
      url="app.statio.dev/dashboard/settings/access-control"
      nav="Access Control"
      overlay={
        <div className="pw lw-over" aria-hidden="true">
          <div className="pw-bar">
            <i />
            <i />
            <i />
            <span>Activity</span>
          </div>
          <div className="pw-body">
            <div className="lw-grow">
              <div className="pw-ev">
                <div className="r1">
                  <span className="pw-tag pw-no">failed</span>
                  <b className="s-data">db.deleteRows</b>
                  <span className="meta">3 ms · 403</span>
                </div>
                <div className="r2">agent ci-agent · production</div>
                <div className="err">You don&apos;t have permission to call tool &apos;db.deleteRows&apos;</div>
              </div>
            </div>
            <div className="pw-ev">
              <div className="r1">
                <span className="pw-tag pw-ok">allowed</span>
                <b className="s-data">db.selectRows</b>
                <span className="meta">42 ms · 200</span>
              </div>
              <div className="r2">agent ci-agent · production</div>
            </div>
          </div>
        </div>
      }
    >
      <div className="lw-page">
        <div className="pw-head">
          <b>Manage Access</b>
          <span className="s-data">ci-agent → postgres</span>
        </div>
        <div className="pw-facts">
          <span>
            Can invoke <b>Yes</b>
          </span>
          <span>
            Direct Grant <b>Yes</b>
          </span>
          <span>
            Team Access <b>None</b>
          </span>
        </div>
        <div className="pw-label">
          <span>Per-tool access</span>
          <span>EXECUTE</span>
        </div>
        {tools.map((t) => (
          <div className="pw-tool" key={t}>
            <span>{t}</span>
            <span className="pw-tag pw-ok">via server access</span>
            <span className="pw-btn">Deny</span>
          </div>
        ))}
        <div className="pw-tool lw-hit">
          <span>db.deleteRows</span>
          <Swap
            k="tag"
            a={<span className="pw-tag pw-ok">via server access</span>}
            b={<span className="pw-tag pw-no">denied</span>}
          />
          <span className="lw-target">
            <Swap k="btn" a={<span className="pw-btn">Deny</span>} b={<span className="pw-btn">Allow</span>} />
            <Cursor id="deny" />
          </span>
        </div>
        <div className="pw-tool">
          <span>db.describeTable</span>
          <span className="pw-tag pw-ok">via server access</span>
          <span className="pw-btn">Deny</span>
        </div>
        <p className="pw-help">
          Server-wide access already covers every tool. Turning a tool off here records a deny for this member, which
          overrides the server-wide grant for that tool only.
        </p>
      </div>
    </LiveFrame>
  );
}

/* ── 2. Dashboard: calls tick up, then open Activity ─────────────────────── */

const RECENT: [string, string, string][] = [
  ['Postgres', 'postgres', 'active'],
  ['GitHub', 'github', 'active'],
  ['Linear', 'linear', 'active'],
  ['Billing API', 'billing-api', 'draft'],
];

const STREAM: [boolean, string, string, number, number, string][] = [
  [true, 'issues.list', 'linear', 38, 200, 'user dana@acme · production'],
  [true, 'db.listTables', 'postgres', 51, 200, 'agent support-bot · production'],
  [false, 'db.deleteRows', 'postgres', 3, 403, 'agent ci-agent · production'],
  [true, 'repos.getContent', 'github', 64, 200, 'agent ci-agent · production'],
];

const OLDER: [boolean, string, string, number, number, string][] = [
  [true, 'db.selectRows', 'postgres', 42, 200, 'agent ci-agent · production'],
  [true, 'issues.create', 'github', 61, 200, 'user alex@acme · production'],
];

function Ev({ row: [ok, tool, server, ms, code, caller] }: { row: (typeof STREAM)[number] }) {
  return (
    <div className="pw-ev">
      <div className="r1">
        <span className={`pw-tag ${ok ? 'pw-ok' : 'pw-no'}`}>{ok ? 'allowed' : 'failed'}</span>
        <b className="s-data">{tool}</b>
        <span>on {server}</span>
        <span className="meta">
          {ms} ms · {code}
        </span>
      </div>
      <div className="r2">{caller}</div>
    </div>
  );
}

export function LiveDashboardWindow() {
  return (
    <LiveFrame
      className="lw-dash"
      label="API calls on the Overview page count up, then the Activity feed opens and new tool calls stream in"
      url="app.statio.dev/dashboard"
      urlTo="app.statio.dev/dashboard/activity"
      nav="Overview"
      navTo="Activity"
    >
      <div className="lw-views">
        <div className="lw-page lw-v1">
          <div className="lw-title">
            <b>Overview</b>
            <span>Your MCP infrastructure at a glance.</span>
          </div>
          <div className="lw-stats">
            <div>
              <small>Total MCPs</small>
              <b>8</b>
            </div>
            <div className="lw-live">
              <small>API Calls</small>
              <b>
                <Ticker from={12480} to={12957} start={0.4} end={5.4} />
              </b>
              <em>This month</em>
            </div>
            <div>
              <small>Active</small>
              <b>7</b>
            </div>
            <div>
              <small>Draft</small>
              <b>1</b>
            </div>
          </div>
          <div className="lw-sub">
            <b>Recent MCPs</b>
            <span>View all</span>
          </div>
          <div className="lw-list">
            {RECENT.map(([name, slug, status]) => (
              <div key={slug}>
                <span>
                  <b>{name}</b> <span className={`pw-tag ${status === 'active' ? 'pw-ok' : 'pw-off'}`}>{status}</span>
                </span>
                <span className="s-data">{slug}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lw-page lw-v2">
          <div className="lw-title">
            <b>Activity</b>
            <span>Every tool call through the gateway, who called what, and whether it was allowed</span>
          </div>
          <div className="lw-feed">
            {[...STREAM].reverse().map((r, i) => (
              <div key={r[1]} className={`lw-in lw-in-${STREAM.length - i}`}>
                <Ev row={r} />
              </div>
            ))}
            {OLDER.map((r) => (
              <Ev key={r[1]} row={r} />
            ))}
          </div>
        </div>
      </div>
    </LiveFrame>
  );
}

/* ── 3. MCP generation: spec URL in, a live server out ───────────────────── */

const SPEC = 'https://api.acme.dev/openapi.yaml';
const MODES = [
  ['Per Endpoint', 'One tool per API endpoint (best for small APIs)'],
  ['Generic', 'Three generic tools for any endpoint (best for large APIs)'],
  ['Curated', 'Hand-picked tools with fallback for remaining endpoints'],
];
const STAGES = ['Generate', 'Build', 'Deploy', 'Grant access', 'Discover tools', 'Moving traffic', 'Done'];

export function LiveGenerateWindow() {
  return (
    <LiveFrame
      className="lw-gen"
      label="an OpenAPI spec URL becomes a preview of 214 endpoints served by three tools, then the server deploys stage by stage"
      url="app.statio.dev/dashboard/mcps/new"
      nav="My MCPs"
    >
      <div className="lw-views">
        <div className="lw-page lw-v1">
          <div className="lw-title">
            <b>Generate from OpenAPI</b>
            <span>
              Choose · <u>Configure</u> · Preview
            </span>
          </div>
          <div className="lw-form">
            <label>
              <small>Display Name</small>
              <span className="lw-in-f">Acme API</span>
            </label>
            <label>
              <small>URL-safe Name</small>
              <span className="lw-in-f s-data">acme</span>
            </label>
          </div>
          <label className="lw-field">
            <small>OpenAPI Spec URL</small>
            <span className="lw-in-f s-data">
              <span className="lw-type" style={{ ['--n' as string]: SPEC.length }}>
                {SPEC}
              </span>
            </span>
          </label>
          <small className="lw-flabel">Generation Mode</small>
          <div className="lw-modes">
            {MODES.map(([m, d]) => (
              <div key={m} className={`lw-mode lw-mode-${m.split(' ')[0].toLowerCase()}`}>
                <i />
                <span>
                  <b>{m}</b>
                  <span>{d}</span>
                </span>
                {m === 'Generic' && <Cursor id="mode" />}
              </div>
            ))}
          </div>
          <div className="lw-actions">
            <span className="pw-btn">Cancel</span>
            <span className="lw-pri">
              Validate &amp; Preview →<Cursor id="validate" />
            </span>
          </div>
        </div>
        <div className="lw-page lw-v2">
          <div className="lw-title">
            <b>Preview Generation</b>
            <span>
              Choose · Configure · <u>Preview</u>
            </span>
          </div>
          <div className="lw-stats lw-stats-3">
            <div>
              <small>Endpoints</small>
              <b>
                <Ticker from={0} to={214} start={5.1} end={6.3} />
              </b>
            </div>
            <div>
              <small>Tools</small>
              <b>3</b>
            </div>
            <div>
              <small>Types</small>
              <b>
                <Ticker from={0} to={96} start={5.1} end={6.3} />
              </b>
            </div>
          </div>
          <div className="lw-base">
            <span>
              <small>Generated tools will call</small>
              <b className="s-data">https://api.acme.dev</b>
            </span>
            <span className="pw-btn">Change</span>
          </div>
          <div className="pw-label">
            <span>Generated Tools</span>
          </div>
          {['acme_list_schemas', 'acme_get_schema', 'acme_api_call'].map((t) => (
            <div className="pw-tool lw-gt" key={t}>
              <span>{t}</span>
            </div>
          ))}
          <div className="lw-stack">
            <div className="lw-actions lw-create">
              <span className="pw-btn">Back</span>
              <span className="lw-pri">
                Create MCP
                <Cursor id="create" />
              </span>
            </div>
            <div className="lw-stages">
              {STAGES.map((s, i) => (
                <span key={s} className={`lw-st lw-st-${i + 1}`}>
                  <i />
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LiveFrame>
  );
}
